/**
 * The Publish panel (R3, screen 11): a 400px drawer that says what
 * publishing would change — "Since v{n} · {k} changes", one line per
 * section, card, repeating group, field, rule and appearance setting — and
 * holds every action the draft bar used to hold: Publish v{n+1}, Test it
 * first, Save draft now, Discard the draft, and the version list with
 * Restore (the whole history is one link away, on Settings › Versions).
 *
 * The change list comes from the server (`POST /:id/changes`), computed
 * over the working copy the workspace sends, so unsaved edits count. The
 * panel never decides anything about the draft: publish, save, discard and
 * restore are the workspace's own handlers.
 *
 * Document and Workflow are not in the list because they never ride the
 * draft — they save live — and the panel says so rather than leaving a
 * reader to wonder why a placement they moved is missing.
 */
import { Link } from 'react-router-dom';
import type { FormChange, FormDetail } from '../../api/forms';
import type { PlanLimitBody } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { Drawer, SectionLabel } from '../../components/fs';
import { VersionList } from './FormVersions';
import type { DraftState, SaveState } from './FormWorkspace';

export interface ChangeSummary {
  /** the live version the list is relative to (0 = never published) */
  since: number;
  count: number;
  list: FormChange[];
}

const KIND_WORD: Record<FormChange['kind'], string> = {
  section: 'Section', card: 'Card', widget: 'Repeating group', field: 'Field', rule: 'Rule', appearance: '', document: 'Document', step: 'Step', workflow: '',
};
const OP_GLYPH: Record<FormChange['op'], string> = { added: '+', changed: '~', removed: '−' };

/** One change as the panel prints it: "+ Field “Student ID” · Your details". */
export function changeLine(c: FormChange): string {
  const kind = KIND_WORD[c.kind];
  const what = kind ? `${kind} “${c.label}”` : c.label;
  return c.detail ? `${what} · ${c.detail}` : what;
}

export default function PublishDrawer({
  form, version, changes, loading, error, state, save, busy, limitHit, versionsBase,
  onClose, onPublish, onTest, onSave, onDiscard, onRestore,
}: {
  form: FormDetail;
  /** the latest publish's ordinal (0 = never published) */
  version: number;
  changes: ChangeSummary | null;
  loading: boolean;
  /** why the list could not be loaded, if it could not */
  error: string;
  state: DraftState;
  save: SaveState;
  busy: boolean;
  /** the 402 the last publish was refused with, until one succeeds */
  limitHit: PlanLimitBody | null;
  /** the Versions page, for "All versions →" */
  versionsBase: string;
  onClose: () => void;
  onPublish: () => void;
  /** open the builder in Test mode */
  onTest: () => void;
  onSave: () => void;
  onDiscard: () => void;
  /** load version `v` into the draft (the workspace confirms nothing further; the list did) */
  onRestore: (v: number) => void;
}) {
  const next = version + 1;
  const never = form.status !== 'published';
  const count = changes?.count ?? null;
  const nothing = count === 0;

  return (
    <Drawer narrow title="Publish changes" onClose={onClose} className="fw-publish">
      <div className="fw-publish-body">
        <SectionLabel>
          {never ? 'First publish' : `Since v${changes?.since ?? version}`}
          {count != null && ` · ${count} ${count === 1 ? 'change' : 'changes'}`}
        </SectionLabel>
        {loading && !changes && <div className="fs-cell-sub">Working out what changed…</div>}
        {error && !changes && <div className="fs-cell-sub" role="alert">{error}</div>}
        {changes && nothing && (
          <div className="fs-cell-sub">
            {never ? 'Nothing on this form yet. Add a field, then publish it.' : 'Nothing new to publish. The live version matches the editor.'}
          </div>
        )}
        {changes && !nothing && (
          <ul className="fw-changes" aria-label="Changes since the live version">
            {changes.list.map((c, i) => (
              <li key={`${c.kind}:${c.key}:${i}`} className={`fw-change ${c.op}`}>
                <span className="fw-change-op" aria-hidden="true">{OP_GLYPH[c.op]}</span>
                <span className="fw-change-text">
                  <span className="fs-sr-only">{c.op} </span>{changeLine(c)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {limitHit && <div className="fw-publish-limit"><PlanLimitBlock body={limitHit} /></div>}
        {save.kind === 'conflict' && (
          <div className="fs-cell-sub fw-publish-conflict" role="alert">
            Someone else saved this form since you opened it. Choose whose draft to keep (the button beside the form's name) before publishing.
          </div>
        )}

        <div className="fw-publish-actions">
          <button className="fs-btn primary fw-publish-go" disabled={busy || save.kind === 'conflict'} onClick={onPublish}>
            {version > 0 ? `Publish v${next}` : 'Publish now'}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={onTest}>Test it first</button>
          <div className="fs-cell-sub faint fw-publish-note">
            People part-way through will see this version when they next open the form.
          </div>
        </div>

        <div className="fw-publish-draft">
          <SectionLabel>This draft</SectionLabel>
          <div className="fw-publish-draftrow">
            <button className="fs-btn neutral sm" disabled={busy || state === 'clean' || save.kind === 'saving'} onClick={onSave}
              title="Drafts save themselves; this saves right now">
              Save draft now
            </button>
            {state !== 'clean' && (
              <button className="fs-btn danger sm" disabled={busy} onClick={onDiscard} title="Throw the draft away and reload what is published">
                Discard the draft
              </button>
            )}
            {state === 'clean' && <span className="fs-cell-sub faint">Nothing unsaved.</span>}
          </div>
        </div>

        <div className="fw-publish-versions">
          <div className="fw-publish-versions-head">
            <SectionLabel>Versions</SectionLabel>
            <Link className="fs-btn text sm" to={versionsBase} onClick={onClose}>All versions →</Link>
          </div>
          <VersionList formId={form.id} compact limit={5} busy={busy} onRestore={onRestore} />
        </div>
      </div>
    </Drawer>
  );
}
